import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";
import { toasts } from "../components/AppToast";

import { FiCheckCircle, FiGlobe, FiLayers } from "react-icons/fi";

export default function DynamicMLInfos() {
  const [sideOpen, setSideOpen] = useState(true);
  const [activePage, setActivePage] = useState("Dynamic Informations");
  const [user, setUser] = useState(null);

  const [modulelist, setModulelist] = useState("");
  const [langid, setLangid] = useState("");
  const [langname, setLangname] = useState("");

  const [sections, setSections] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [records, setRecords] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Get current active section name for the badge count banner
  const currentSectionObj = sections.find(
    (sec) => String(sec.mlsec_id) === String(modulelist)
  );
  const sectionName = currentSectionObj?.ml_section || "Section";

  
  // AUTH EFFECT
  
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
    setUser(JSON.parse(localStorage.getItem("user") || "null"));
  }, []);

  
  // GET DATA
  
  const getDynamicInformation = async (
    selectedModule = "",
    selectedLanguage = ""
  ) => {
    try {
      setLoading(true);

      const params = {};

      if (selectedModule) {
        params.modulelist = selectedModule;
      }

      if (selectedLanguage) {
        params.langid = selectedLanguage;
      }

      const response = await API.get("/manage-lanaguage/dynamic-translate", {
        params,
      });

      if (!response.data?.success) {
        toasts.error(
          response.data?.message || "Failed to load dynamic information"
        );
        return;
      }

      const data = response.data;

      setModulelist(String(data.modulelist || ""));
      setLangid(String(data.langid || ""));
      setLangname(data.langname || "");

      setSections(data.sections || []);
      setLanguages(data.languages || []);
      setRecords(data.records || []);
    } catch (error) {
      console.error("Get Dynamic Information Error:", error);

      toasts.error(
        error.response?.data?.message || "Failed to load dynamic information"
      );
    } finally {
      setLoading(false);
    }
  };

  
  // INITIAL LOAD
  
  useEffect(() => {
    getDynamicInformation();
  }, []);

  
  // SECTION CHANGE
  
  const handleSectionChange = (e) => {
    const selectedModule = e.target.value;

    setModulelist(selectedModule);

    if (!selectedModule) {
      setRecords([]);
      return;
    }

    getDynamicInformation(selectedModule, langid);
  };

  
  // LANGUAGE CHANGE
  
  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;

    setLangid(selectedLanguage);

    if (!selectedLanguage) {
      setRecords([]);
      return;
    }

    getDynamicInformation(modulelist, selectedLanguage);
  };

  
  // VALUE CHANGE
  
  const handleValueChange = (index, value) => {
    setRecords((previousRecords) =>
      previousRecords.map((record, recordIndex) => {
        if (recordIndex !== index) {
          return record;
        }

        return {
          ...record,
          translatedValue: value,
        };
      })
    );
  };

  
  // DESCRIPTION CHANGE
  
  const handleDescriptionChange = (index, value) => {
    setRecords((previousRecords) =>
      previousRecords.map((record, recordIndex) => {
        if (recordIndex !== index) {
          return record;
        }

        return {
          ...record,
          translatedDesc: value,
        };
      })
    );
  };

  
  // EXTRA DESCRIPTION CHANGE
  
  const handleExtraDescriptionChange = (index, value) => {
    setRecords((previousRecords) =>
      previousRecords.map((record, recordIndex) => {
        if (recordIndex !== index) {
          return record;
        }

        return {
          ...record,
          translatedExtraDesc: value,
        };
      })
    );
  };

  
  // SUBMIT
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!modulelist) {
      toasts.warning("Section Name is required");
      return;
    }

    if (!langid) {
      toasts.warning("Language is required");
      return;
    }

    if (!records.length) {
      toasts.warning("No records available");
      return;
    }

    // Validate required translation
    for (const record of records) {
      if (!String(record.translatedValue || "").trim()) {
        toasts.warning(`Translation is required for "${record.englishValue}"`);
        return;
      }
    }

    try {
      setSaving(true);

      const payload = {
        modulelist: String(modulelist),
        langid: String(langid),
        records: records.map((record) => ({
          id: record.id,
          value: record.translatedValue || "",
          desc: record.translatedDesc || "",
          extraDesc: record.translatedExtraDesc || "",
        })),
      };

      const response = await API.post(
        "/manage-lanaguage/dynamic-translate",
        payload
      );

      if (response.data?.success) {
        toasts.success(
          response.data.message || "Dynamic information saved successfully"
        );

        await getDynamicInformation(modulelist, langid);
      } else {
        toasts.error(
          response.data?.message || "Failed to save dynamic information"
        );
      }
    } catch (error) {
      console.error("Save Dynamic Information Error:", error);

      toasts.error(
        error.response?.data?.message || "Failed to save dynamic information"
      );
    } finally {
      setSaving(false);
    }
  };

  
  // CLEAR
  
  // const handleClear = () => {
  //   setRecords((previousRecords) =>
  //     previousRecords.map((record) => ({
  //       ...record,
  //       translatedValue: "",
  //       translatedDesc: "",
  //       translatedExtraDesc: "",
  //     }))
  //   );
  // };

  return (
    <Layout
      sideOpen={sideOpen}
      setSideOpen={setSideOpen}
      activePage={activePage}
      setActivePage={setActivePage}
      user={user}
    >
      <main className="dashboard-main">
        <div className="user-form-page">
          {/* HEADER */}
          <div className="user-form-header">
            <div className="d-flex align-items-center gap-2">
              <div
                className="form-card-icon"
                style={{
                  color: "#0C807C",
                  background: "rgba(12, 128, 124, 0.1)",
                }}
              >
                <FiGlobe size={24} color="#0C807C" />
              </div>
              <div>
                <h2 className="um-modal-title mb-0">Dynamic Informations</h2>
                <p className="form-card-sub mb-0">
                  (Manage dynamic multilingual translations and entries)
                </p>
              </div>
            </div>
          </div>

          {/* FORM CARD */}
          <div className="user-form-card">
            <form id="frmmlinfos" onSubmit={handleSubmit} autoComplete="off">
              {/* CONFIGURATION SETTINGS */}
              <div className="sec-div">
                <span>Configuration Settings</span>
              </div>

              <div className="row g-4">
                {/* SECTION */}
                <div className="col-xl-6 col-lg-6 col-md-6">
                  <label htmlFor="cmbsection" className="um-label">
                    Section Name <span className="text-danger">*</span>
                  </label>

                  <select
                    name="cmbsection"
                    id="cmbsection"
                    className="um-input"
                    value={modulelist}
                    onChange={handleSectionChange}
                    disabled={loading || saving}
                  >
                    <option value="">-- Select Section --</option>

                    {sections.map((section) => (
                      <option key={section.mlsec_id} value={section.mlsec_id}>
                        {section.ml_section}
                      </option>
                    ))}
                  </select>
                </div>

                {/* LANGUAGE */}
                <div className="col-xl-6 col-lg-6 col-md-6">
                  <label htmlFor="cmbLanguage" className="um-label">
                    Language <span className="text-danger">*</span>
                  </label>

                  <select
                    name="cmbLanguage"
                    id="cmbLanguage"
                    className="um-input"
                    value={langid}
                    onChange={handleLanguageChange}
                    disabled={loading || saving}
                  >
                    <option value="">-- Select Language --</option>

                    {languages.map((language) => (
                      <option key={language.lang_id} value={language.lang_id}>
                        {language.lang_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* TRANSLATION SECTION */}
              <div className="sec-div mt-4">
                <span>Translate From English To {langname || "Language"}</span>
              </div>

              <div className="text-muted small mb-3">
                Enter the dynamic translation equivalents below.
              </div>

              {/* LOADING */}
              {loading && (
                <div className="text-center py-5">
                  <span className="spinner" />
                  <div className="mt-2 text-muted">
                    Loading dynamic information...
                  </div>
                </div>
              )}

              {/* NO RECORDS */}
              {!loading && records.length === 0 && (
                <div className="alert alert-info">No records found.</div>
              )}

              {/* RECORDS LIST WITH THEME BANNER AND BADGE */}
              {!loading && records.length > 0 && (
                <>
                  <div
                    className="d-flex align-items-center justify-content-between mb-3 p-3 rounded border"
                    style={{
                      backgroundColor: "rgba(12, 128, 124, 0.04)",
                      borderColor: "rgba(12, 128, 124, 0.2)",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FiLayers size={18} style={{ color: "#0C807C" }} />
                      <span className="fw-bold" style={{ color: "#0C807C" }}>
                        Section Name: {sectionName}
                      </span>
                    </div>
                    <span
                      className="badge px-3 py-2 rounded-pill shadow-sm"
                      style={{
                        backgroundColor: "#0C807C",
                        color: "#ffffff",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                      }}
                    >
                      {records.length} {records.length === 1 ? "Item" : "Items"}
                    </span>
                  </div>

                  {/* UNIFIED TABLE HEADER */}
                  <div className="sec-div mt-3 mb-3">
                    <div className="row w-100 m-0">
                      <div className="col-md-5">
                        <span>ENGLISH EQUIVALENT *</span>
                      </div>
                      <div className="col-md-7">
                        <span>
                          {(langname || "LANGUAGE").toUpperCase()} EQUIVALENT *
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="row g-4">
                    {records.map((record, index) => (
                      <div className="col-12" key={record.id}>
                        <div className="p-3 border rounded bg-light">
                          <div className="row g-3">
                            {/* ENGLISH */}
                            <div className="col-md-5">
                              <input
                                type="text"
                                className="um-input bg-white"
                                value={record.englishValue || ""}
                                readOnly
                              />

                              <input
                                type="hidden"
                                name="hidsecid[]"
                                value={record.id}
                              />

                              {record.englishDesc && (
                                <div className="mt-2">
                                  <div className="small text-muted mb-1">
                                    Description
                                  </div>
                                  <textarea
                                    className="um-input bg-white"
                                    rows="3"
                                    value={record.englishDesc || ""}
                                    readOnly
                                  />
                                </div>
                              )}

                              {record.englishExtraDesc && (
                                <div className="mt-2">
                                  <div className="small text-muted mb-1">
                                    Extra Description
                                  </div>
                                  <textarea
                                    className="um-input bg-white"
                                    rows="3"
                                    value={record.englishExtraDesc || ""}
                                    readOnly
                                  />
                                </div>
                              )}
                            </div>

                            {/* TRANSLATION */}
                            <div className="col-md-7">
                              {/* VALUE */}
                              <div className="mb-3">
                                <input
                                  type="text"
                                  name="txtsecval[]"
                                  className="um-input"
                                  maxLength="500"
                                  value={record.translatedValue || ""}
                                  onChange={(e) =>
                                    handleValueChange(index, e.target.value)
                                  }
                                  disabled={saving}
                                  placeholder={`Enter ${
                                    langname || "translation"
                                  } equivalent`}
                                />
                              </div>

                              {/* DESCRIPTION */}
                              {record.hasDescription && (
                                <div className="mb-3">
                                  <div className="small text-muted mb-1">
                                    Description
                                  </div>
                                  <textarea
                                    name="txtsecdesc[]"
                                    className="um-input"
                                    rows="3"
                                    value={record.translatedDesc || ""}
                                    onChange={(e) =>
                                      handleDescriptionChange(
                                        index,
                                        e.target.value
                                      )
                                    }
                                    disabled={saving}
                                    placeholder="Enter description equivalent"
                                  />
                                </div>
                              )}

                              {/* EXTRA DESCRIPTION */}
                              {record.hasExtraDescription && (
                                <div className="mb-3">
                                  <textarea
                                    name="txtsecextradesc[]"
                                    className="um-input"
                                    rows="3"
                                    value={record.translatedExtraDesc || ""}
                                    onChange={(e) =>
                                      handleExtraDescriptionChange(
                                        index,
                                        e.target.value
                                      )
                                    }
                                    disabled={saving}
                                    placeholder="Enter extra description equivalent"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* FORM FOOTER BUTTONS */}
              {!loading && records.length > 0 && (
                <div className="user-form-footer mt-4">
                  {/* <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={handleClear}
                    disabled={saving}
                  >
                    <FiX />
                    Clear
                  </button> */}

                  <button
                    type="submit"
                    className="um-btn-primary"
                    disabled={saving || loading}
                    style={{
                      backgroundColor: "#0C807C",
                      borderColor: "#0C807C",
                    }}
                  >
                    {saving ? (
                      <>
                        <span className="spinner" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle />
                        Submit Translations
                      </>
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </main>

    
    </Layout>
  );
}
